import {
  Box,
  Breadcrumbs,
  CardMedia,
  FormControl,
  Input,
  InputAdornment,
  Link,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import { ChangeEvent, useEffect, useMemo, useState, MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DateRangePickerDay, { DateRangePickerDayProps } from '@mui/lab/DateRangePickerDay';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import StaticDateRangePicker from '@mui/lab/StaticDateRangePicker';
import { CommonLayout } from '../commonComponents';
import { ColumnType, DataTable } from '../DataTable';
import { getEntityInvestors, getEntitySummary } from '../../redux-store/actions/application';
import { getFundsList } from '../../redux-store/actions/funds';
import { getDistributorById } from '../../redux-store/actions/userManagement';
import {
  ApplicationSummaryViewAs,
  EntityInvestorItem,
  EntityInvestorsResponse,
  EntitySummaryResponse,
} from '../../redux-store/types/application';
import { FundProps, GetFundsListResponseBody } from '../../redux-store/types/funds';
import { DistributorListType, SubDistributorListType } from '../../redux-store/types/api-types';
import { APPLICATION_LISTING_STATUS } from '../../utils/constant';
import { formatCurrency } from '../../utils/utilityFunctions';
import { useDebounce } from '../../utils/useDebounce';
import { formatShortDate } from '../../utils/date';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const RangeDay = (props: DateRangePickerDayProps<Date>) => (
  <DateRangePickerDay
    {...props}
    sx={{
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
        backgroundColor: '#E8ECF0',
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart, &.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd':
        {
          backgroundColor: 'transparent',
        },
      '& .MuiDateRangePickerDay-day': {
        fontSize: 13,
        fontWeight: 500,
        color: '#5F6B7A',
        borderRadius: '4px',
        '&.Mui-selected': {
          backgroundColor: '#2057A6',
          color: '#FFFFFF',
          fontWeight: 600,
          '&:hover, &:focus': {
            backgroundColor: '#2057A6',
          },
        },
        '&.MuiDateRangePickerDay-dayInsideRangeInterval': {
          color: '#293139',
          fontWeight: 600,
        },
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart .MuiDateRangePickerDay-day': {
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day': {
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day':
        {
          borderRadius: '4px',
        },
    }}
  />
);

const PAGE_LIMIT = 10;

export type EntityDetailsLocationState = {
  entityId: number | string;
  viewAs: ApplicationSummaryViewAs;
  entityName: string;
  isCorporate?: boolean;
};

const formatAmount = (amount?: number | null): string => {
  return `₹ ${formatCurrency(amount || 0, 'INR')}`;
};

const getStatusPillStyles = (status: string) => {
  if (['completed', 'signed'].includes(status)) {
    return { label: 'Completed', bg: '#0DB9751A', color: '#0DB975' };
  }
  if (status === 'rejected') {
    return { label: 'Rejected', bg: '#FEE4E2', color: '#B42318' };
  }
  if (status === 'draft') {
    return { label: 'Draft', bg: '#2057A61A', color: '#2057A6' };
  }
  if (status === 'reviewed') {
    return { label: 'Reviewed', bg: '#FFA0001A', color: '#FFA000' };
  }
  return {
    label: APPLICATION_LISTING_STATUS[status] || status,
    bg: '#2057A61A',
    color: '#2057A6',
  };
};

export default function EntityDetails(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const { state } = useLocation<EntityDetailsLocationState>();

  const entityId = state?.entityId;
  const viewAs = state?.viewAs || 'distributor';
  const entityName = state?.entityName || 'Details';
  const isRm = viewAs === 'rm';
  const pageTitle = isRm ? `${entityName} Details` : `${entityName} Details`;

  const [summary, setSummary] = useState<EntitySummaryResponse | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [investors, setInvestors] = useState<EntityInvestorItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [noOfPages, setNoOfPages] = useState(1);
  const [search, setSearch] = useState('');
  const [schemeId, setSchemeId] = useState('');
  const [funds, setFunds] = useState<FundProps[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const debounceSearch = useDebounce(search, 500);
  const isCorporate = state?.isCorporate === true;
  const [subDistributors, setSubDistributors] = useState<SubDistributorListType[]>([]);
  const [selectedSubDistributorId, setSelectedSubDistributorId] = useState<string>('');

  const getSubDistributorName = (subDistributor?: SubDistributorListType | null): string =>
    subDistributor?.user?.firstName || 'N/A';

  useEffect(() => {
    if (!entityId || !isCorporate) {
      setSubDistributors([]);
      setSelectedSubDistributorId('');
      return;
    }

    let isActive = true;

    const fetchSubDistributors = async () => {
      try {
        const distributorRes = (await dispatch(
          getDistributorById(entityId)
        )) as unknown as DistributorListType;

        if (!isActive) return;

        setSubDistributors(distributorRes?.subdistributors || []);
      } catch (error) {
        console.error(error);

        if (isActive) {
          setSubDistributors([]);
        }
      }
    };

    fetchSubDistributors();

    return () => {
      isActive = false;
    };
  }, [entityId, isCorporate, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!entityId) {
      history.replace('/user-statistics');
    }
  }, [entityId, history]);

  useEffect(() => {
    let isActive = true;
    (async function () {
      try {
        const { funds: fundsList } = (await dispatch(
          getFundsList()
        )) as unknown as GetFundsListResponseBody;
        if (!isActive) return;
        setFunds(fundsList || []);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!entityId) return;
    let isActive = true;
    (async function () {
      try {
        setIsSummaryLoading(true);
        const response = (await dispatch(
          getEntitySummary({ viewAs, entityId })
        )) as unknown as EntitySummaryResponse;
        if (!isActive) return;
        setSummary(response);
      } catch (e) {
        console.error((e as Error).message);
        if (isActive) setSummary(null);
      } finally {
        if (isActive) setIsSummaryLoading(false);
      }
    })();
    return () => {
      isActive = false;
    };
  }, [entityId, viewAs]);

  useEffect(() => {
    if (!entityId) return;
    let isActive = true;
    (async function () {
      try {
        setIsLoading(true);
        const params: {
          viewAs: ApplicationSummaryViewAs;
          entityId: string | number;
          page: number;
          limit: number;
          schemeId?: string;
          from?: string;
          to?: string;
          search?: string;
          subdistributorId?: string;
        } = {
          viewAs,
          entityId,
          page: currentPage,
          limit: PAGE_LIMIT,
        };
        if (schemeId) params.schemeId = schemeId;
        if (fromDate) {
          const startOfDay = new Date(fromDate);
          startOfDay.setHours(0, 0, 0, 0);
          params.from = startOfDay.toISOString();
        }
        if (toDate) {
          const endOfDay = new Date(toDate);
          endOfDay.setHours(23, 59, 59, 999);
          params.to = endOfDay.toISOString();
        }
        if (debounceSearch.trim()) params.search = debounceSearch.trim();
        if (selectedSubDistributorId) {
          params.subdistributorId = selectedSubDistributorId;
        }

        const response = (await dispatch(
          getEntityInvestors(params)
        )) as unknown as EntityInvestorsResponse;
        if (!isActive) return;
        setInvestors(response?.data || []);
        setNoOfPages(response?.pages || 1);
      } catch (e) {
        console.error((e as Error).message);
        if (isActive) {
          setInvestors([]);
          setNoOfPages(1);
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    })();
    return () => {
      isActive = false;
    };
  }, [
    entityId,
    viewAs,
    currentPage,
    schemeId,
    fromDate,
    toDate,
    debounceSearch,
    selectedSubDistributorId,
  ]);

  const metricCards = [
    {
      label: 'TOTAL VALUE',
      amount: summary?.totalValue?.commitmentAmount || 0,
      count: summary?.totalValue?.count || 0,
      bg: 'linear-gradient(95.65deg, #E8F1FF 1.71%, #D2E4FF 97.94%)',
      color: '#1450A7',
      icon: '/images/total-value.svg',
    },
    {
      label: 'PROCESSED',
      amount: summary?.processed?.commitmentAmount || 0,
      count: summary?.processed?.count || 0,
      bg: 'linear-gradient(95.65deg, #E0FCE9 1.71%, #B3F3D0 97.94%)',
      color: '#004B50',
      icon: '/images/completed.svg',
    },
    {
      label: 'PIPELINE',
      amount: summary?.pipeline?.commitmentAmount || 0,
      count: summary?.pipeline?.count || 0,
      bg: 'linear-gradient(95.65deg, #FFF8E1 1.71%, #FFECB8 97.94%)',
      color: '#7A4100',
      icon: '/images/pipeline.svg',
    },
    {
      label: 'FAILED',
      amount: summary?.failed?.commitmentAmount || 0,
      count: summary?.failed?.count || 0,
      bg: 'linear-gradient(135deg, #FCE8E8 0%, #F9D4D4 100%)',
      color: '#B42318',
      icon: '/images/failed.svg',
    },
  ];

  const tableHeader: ColumnType[] = useMemo(
    () => [
      {
        header: 'Application Number',
        valueGetter: (row: EntityInvestorItem) => row.applicationNumber || 'N/A',
      },
      {
        header: 'Investor Name',
        valueGetter: (row: EntityInvestorItem) => row.investorName || 'N/A',
      },
      {
        header: 'PAN',
        valueGetter: (row: EntityInvestorItem) => row.pan || 'N/A',
      },
      {
        header: 'Commitment Amount',
        valueGetter: (row: EntityInvestorItem) => formatAmount(row.commitmentAmount),
      },
      {
        header: 'Investment Amount',
        valueGetter: (row: EntityInvestorItem) => formatAmount(row.investmentAmount),
      },
      {
        header: 'Status',
        renderCell: (row: EntityInvestorItem) => {
          const pill = getStatusPillStyles(row.status);
          return (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: { xs: 0.75, sm: 1.5 },
                py: { xs: 0.25, sm: 0.5 },
                borderRadius: '999px',
                fontSize: { xs: 9, sm: 12 },
                fontWeight: 600,
                lineHeight: { xs: 1.2, sm: 1.4 },
                whiteSpace: 'nowrap',
                background: pill.bg,
                color: pill.color,
                fontFamily: 'Roboto, sans-serif',
              }}>
              {pill.label}
            </Box>
          );
        },
      },
    ],
    []
  );

  const dateLabel =
    fromDate || toDate
      ? `${fromDate ? formatShortDate(fromDate) : 'From'} - ${
          toDate ? formatShortDate(toDate) : 'To'
        }`
      : 'From - To Date';

  if (!entityId) {
    return <></>;
  }

  return (
    <CommonLayout contentBgcolor="#FFFFFF">
      <Box
        sx={{
          fontFamily: 'Roboto, sans-serif',
          '& .MuiTypography-root, & .MuiTypography-h6, & .MuiTypography-body1, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiTableCell-root, & .MuiChip-root, & .MuiChip-label, & .MuiLink-root, & .MuiTab-root, & .MuiMenuItem-root, & .MuiFormLabel-root, & .MuiSelect-select, & .MuiPaginationItem-root, & .MuiFormControlLabel-label, & .MuiListItemText-primary, & .MuiBreadcrumbs-root, & .MuiGrid-root':
            {
              fontFamily: 'Roboto, sans-serif !important',
            },
        }}>
        <Breadcrumbs
          separator=">"
          aria-label="breadcrumb"
          sx={{
            mb: 1.5,
            '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
            '.MuiTypography-root, .MuiLink-root': {
              fontSize: 14,
              color: '#111927',
              fontFamily: 'Roboto, sans-serif',
            },
          }}>
          <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
            Home
          </Link>
          <Link component={RouterLink} to="/user-statistics" underline="hover" color="inherit">
            User Statistics
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 500, color: '#6C737F !important' }}>
            {entityName}
          </Typography>
        </Breadcrumbs>

        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: { xs: 22, sm: 28 },
            fontWeight: 700,
            color: '#1A2B3C',
            mb: 2.5,
          }}>
          {pageTitle}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: { xs: 1.5, sm: 2 },
            mb: 3,
            opacity: isSummaryLoading ? 0.7 : 1,
            width: '100%',
          }}>
          {metricCards.map((card) => (
            <Box
              key={card.label}
              sx={{
                backgroundImage: `url(/images/card-background.svg), ${card.bg}`,
                backgroundRepeat: 'no-repeat, no-repeat',
                backgroundPosition: 'center, center',
                backgroundSize: 'cover, cover',
                borderRadius: 3,
                p: { xs: 1.75, sm: 2, lg: 2.5 },
                minHeight: { xs: 110, sm: 120, lg: 140 },
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: 12, sm: 13, lg: 14 },
                  lineHeight: 1.2,
                  color: '#000',
                  textTransform: 'uppercase',
                }}>
                {card.label}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: { xs: 1, sm: 1.5 },
                  width: '100%',
                  minWidth: 0,
                  mt: 1,
                }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: { xs: 18, sm: 20, lg: 24 },
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}>
                    {formatAmount(card.amount)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: { xs: 11, sm: 12, lg: 14 },
                      fontWeight: 400,
                      color: card.color,
                      mt: 0.5,
                      lineHeight: 1.3,
                    }}>
                    of {card.count} applications
                  </Typography>
                </Box>
                <Box
                  component="img"
                  src={card.icon}
                  alt=""
                  sx={{
                    width: { xs: 28, sm: 32, lg: 40 },
                    height: { xs: 28, sm: 32, lg: 40 },
                    flexShrink: 0,
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontStyle: 'normal',
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: 0,
            color: '#111927',
            mb: 2,
          }}>
          My Investors
        </Typography>

        <Box
          sx={{
            bgcolor: 'common.white',
            borderRadius: '12px',
            border: '1px solid #E8ECF0',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
            p: { xs: 2, sm: 3 },
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 3,
              width: '100%',
              minWidth: 0,
            }}>
            {/* Scheme Dropdown */}
            <FormControl
              size="small"
              sx={{
                flex: '1 1 160px',
                minWidth: 0,
                maxWidth: { xs: '100%', sm: 220 },
              }}>
              <Select
                displayEmpty
                value={schemeId}
                onChange={(event: SelectChangeEvent) => {
                  setSchemeId(event.target.value);
                  setCurrentPage(1);
                }}
                IconComponent={KeyboardArrowDownIcon}
                inputProps={{ 'aria-label': 'Filter by scheme' }}
                sx={{
                  height: '36px',
                  borderRadius: '74px',
                  backgroundColor: '#FFFFFF',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  width: '100%',
                  minWidth: 0,

                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D9DEE5',
                    borderRadius: '74px',
                  },

                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B8C0CB',
                  },

                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2057A6',
                    borderWidth: '1px',
                  },

                  '&.Mui-focused, &:focus-within, &:focus-visible': {
                    outline: '3px solid #2057A6 !important',
                    outlineOffset: '2px !important',
                    borderRadius: '74px !important',
                  },

                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    height: '36px',
                    boxSizing: 'border-box',
                    py: 0,
                    minWidth: 0,
                    overflow: 'hidden',
                    paddingRight: '32px !important',
                  },

                  '& .MuiSelect-select:focus': {
                    outline: 'none',
                    backgroundColor: 'transparent',
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}>
                        <CardMedia
                          component="img"
                          src="/images/rupee.svg"
                          alt=""
                          sx={{
                            width: 18,
                            height: 18,
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#293139',
                            fontFamily: 'Roboto, sans-serif',
                          }}>
                          Scheme
                        </Typography>
                      </Box>
                    );
                  }

                  const fund = funds.find((item) => String(item.id) === String(selected));

                  return (
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#293139',
                        fontFamily: 'Roboto, sans-serif',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {fund?.schemeName || 'Scheme'}
                    </Typography>
                  );
                }}>
                <MenuItem value="">
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: '#293139',
                    }}>
                    All Schemes
                  </Typography>
                </MenuItem>

                {funds.map((fund) => (
                  <MenuItem
                    key={fund.id}
                    value={String(fund.id)}
                    sx={{
                      fontSize: 14,
                      fontFamily: 'Roboto, sans-serif',
                    }}>
                    {fund.schemeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isCorporate && (
              <FormControl
                size="small"
                sx={{
                  flex: '1 1 160px',
                  minWidth: 0,
                  maxWidth: { xs: '100%', sm: 220 },
                }}>
                <Select
                  displayEmpty
                  value={selectedSubDistributorId}
                  onChange={(event: SelectChangeEvent) => {
                    setSelectedSubDistributorId(event.target.value);
                    setCurrentPage(1);
                  }}
                  inputProps={{ 'aria-label': 'Filter by sub distributor' }}
                  sx={{
                    height: '36px',
                    borderRadius: '74px',
                    backgroundColor: '#FFFFFF',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    width: '100%',
                    minWidth: 0,

                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D9DEE5',
                      borderRadius: '74px',
                    },

                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#B8C0CB',
                    },

                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2057A6',
                      borderWidth: '1px',
                    },

                    '&.Mui-focused, &:focus-within, &:focus-visible': {
                      outline: '3px solid #2057A6 !important',
                      outlineOffset: '2px !important',
                      borderRadius: '74px !important',
                    },

                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      height: '36px',
                      boxSizing: 'border-box',
                      py: 0,
                      minWidth: 0,
                      overflow: 'hidden',
                      paddingRight: '32px !important',
                    },

                    '& .MuiSelect-select:focus': {
                      outline: 'none',
                      backgroundColor: 'transparent',
                    },
                  }}
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}>
                          <CardMedia
                            component="img"
                            src="/images/sub-dist.svg"
                            alt="Sub Distributor"
                            sx={{
                              width: 18,
                              height: 18,
                            }}
                          />

                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: '#293139',
                              fontFamily: 'Roboto, sans-serif',
                            }}>
                            Sub Distributor
                          </Typography>
                        </Box>
                      );
                    }

                    const selectedSubDistributor = subDistributors.find(
                      (item) => String(item.id) === String(selected)
                    );

                    return (
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#293139',
                          fontFamily: 'Roboto, sans-serif',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {getSubDistributorName(selectedSubDistributor)}
                      </Typography>
                    );
                  }}>
                  <MenuItem value="">
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: '#293139',
                      }}>
                      All Sub Distributors
                    </Typography>
                  </MenuItem>
                  {subDistributors.map((subDistributor) => (
                    <MenuItem key={subDistributor.id} value={String(subDistributor.id)}>
                      {getSubDistributorName(subDistributor)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* From Date - To Date */}
            <Box
              sx={{
                flex: '1 1 220px',
                minWidth: 0,
                maxWidth: { xs: '100%', sm: 280 },
                position: 'relative',
              }}>
              <Box
                component="button"
                type="button"
                aria-label="Select date range filter"
                onClick={(e: MouseEvent<HTMLButtonElement>) => setDateAnchorEl(e.currentTarget)}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  overflow: 'hidden',
                  boxSizing: 'border-box',

                  border: '1px solid #D9DEE5',
                  borderRadius: '74px',
                  backgroundColor: '#FFFFFF',
                  cursor: 'pointer',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: 14,
                  fontWeight: 500,
                  color: fromDate || toDate ? '#293139' : '#5F6B7A',
                  textAlign: 'left',
                  transition: 'border-color 0.2s ease',
                  '&:hover': {
                    borderColor: '#B8C0CB',
                  },
                  '&:focus': {
                    outline: 'none',
                    borderColor: '#2057A6',
                  },
                }}>
                <CardMedia
                  component="img"
                  src="/images/calender.svg"
                  alt=""
                  sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: 12, sm: 14 },
                    fontWeight: 500,
                    color: fromDate || toDate ? '#293139' : '#5F6B7A',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {dateLabel}
                </Typography>
              </Box>
              <Popover
                open={Boolean(dateAnchorEl)}
                anchorEl={dateAnchorEl}
                onClose={() => setDateAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    px: 2,
                    pt: 1.5,
                    pb: 1.5,
                    width: {
                      xs: 300,
                      sm: 320,
                    },
                    borderRadius: '10px',
                    boxShadow: '0px 8px 24px rgba(26, 43, 60, 0.12)',
                    border: '1px solid #E8ECF0',
                    overflow: 'hidden',
                  },
                }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box
                    sx={{
                      '& .MuiPickerStaticWrapper-root': {
                        minWidth: 'unset',
                      },
                      '& .MuiCalendarPicker-root': {
                        width: '100%',
                        maxHeight: 'unset',
                      },
                      '& .MuiPickersCalendarHeader-root': {
                        paddingLeft: 0,
                        paddingRight: 0,
                        marginTop: 0,
                        marginBottom: 0,
                      },
                      '& .MuiPickersCalendarHeader-label': {
                        fontSize: 15,
                        fontWeight: 700,
                        color: '#293139',
                      },
                      '& .MuiDayPicker-header': {
                        justifyContent: 'space-between',
                      },
                      '& .MuiDayPicker-weekDayLabel': {
                        fontSize: 12,
                        color: '#9AA3AF',
                        fontWeight: 500,
                        width: 36,
                        height: 32,
                      },
                      '& .MuiDayPicker-weekContainer': {
                        justifyContent: 'space-between',
                        margin: 0,
                      },
                      '& .MuiDayPicker-slideTransition, & .MuiPickersSlideTransition-root, & .PrivatePickersSlideTransition-root':
                        {
                          minHeight: '240px !important',
                        },
                      '& .MuiDateRangePickerDay-root': {
                        width: 36,
                        height: 36,
                        margin: 0,
                      },
                    }}>
                    <StaticDateRangePicker
                      displayStaticWrapperAs="desktop"
                      calendars={1}
                      allowSameDateSelection
                      value={[fromDate, toDate]}
                      maxDate={new Date()}
                      onChange={(newValue) => {
                        const [start, end] = newValue;
                        setFromDate(start);
                        setToDate(end);
                        setCurrentPage(1);
                      }}
                      renderDay={(_day, dateRangePickerDayProps) => (
                        <RangeDay {...dateRangePickerDayProps} />
                      )}
                      renderInput={(startProps, endProps) => (
                        <>
                          <TextField {...startProps} sx={{ display: 'none' }} />
                          <TextField {...endProps} sx={{ display: 'none' }} />
                        </>
                      )}
                    />
                  </Box>
                </LocalizationProvider>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 0.5,
                  }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => {
                      setFromDate(null);
                      setToDate(null);
                      setCurrentPage(1);
                      setDateAnchorEl(null);
                    }}
                    sx={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2057A6',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: 'Roboto, sans-serif',
                      p: 0,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}>
                    Clear dates
                  </Box>
                </Box>
              </Popover>
            </Box>
            {/* Search */}
            <FormControl
              sx={{
                flex: '2 1 220px',
                minWidth: 0,
                maxWidth: '100%',
              }}>
              <Input
                fullWidth
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by Investor Name/PAN"
                inputProps={{ 'aria-label': 'Search by Investor Name or PAN' }}
                startAdornment={
                  <InputAdornment
                    position="start"
                    sx={{
                      mr: 0.75,
                    }}>
                    <SearchSharpIcon
                      sx={{
                        color: '#7B8794',
                        fontSize: 21,
                      }}
                    />
                  </InputAdornment>
                }
                disableUnderline
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  height: '36px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #D9DEE5',
                  borderRadius: '74px',
                  px: 1.5,
                  fontSize: 14,
                  fontFamily: 'Roboto, sans-serif',
                  transition: 'border-color 0.2s ease',
                  '&:hover': {
                    borderColor: '#B8C0CB',
                  },
                  '&:focus-within': {
                    borderColor: '#2057A6',
                    outline: '3px solid #2057A6 !important',
                    outlineOffset: '2px !important',
                    borderRadius: '74px !important',
                  },
                  '&.Mui-focused': {
                    outline: '3px solid #2057A6 !important',
                    outlineOffset: '2px !important',
                    borderRadius: '74px !important',
                  },
                  '& input': {
                    height: '36px',
                    boxSizing: 'border-box',
                    py: 0,
                    minWidth: 0,
                  },
                  '& input::placeholder': {
                    color: '#8A94A6',
                    opacity: 1,
                  },
                }}
              />
            </FormControl>
            {(schemeId || selectedSubDistributorId || fromDate || toDate || search) && (
              <Box
                component="button"
                type="button"
                aria-label="Clear all filters"
                onClick={() => {
                  setSchemeId('');
                  setSelectedSubDistributorId('');
                  setFromDate(null);
                  setToDate(null);
                  setSearch('');
                  setCurrentPage(1);
                }}
                sx={{
                  border: 'none',
                  background: 'transparent',
                  color: '#2057A6',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  p: 0,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}>
                Clear Filters
              </Box>
            )}
          </Box>

          <DataTable
            tableData={investors}
            tableHeader={tableHeader}
            isLoading={isLoading}
            currentPage={currentPage}
            noOfPages={noOfPages}
            paginationAction={(_event: ChangeEvent<unknown>, value: number) =>
              setCurrentPage(value)
            }
            boxStyles={{ maxHeight: 'none', minHeight: { xs: 'auto', md: '480px' }, minWidth: 0 }}
            tableHeaderCustomStyles={{
              '.MuiTableCell-head': {
                height: '48px',
                padding: '16px',
                fontFamily: 'Roboto, sans-serif',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0.02em',
                color: '#6C737F',
                backgroundColor: '#FFFFFF',
                borderBottom: '0.5px solid #CDCDCD',
                whiteSpace: 'nowrap',
                verticalAlign: 'middle',
              },
            }}
            tableBodyCustomStyles={{
              '.MuiTableRow-root': {
                height: '64px',
                backgroundColor: '#FFFFFF',
              },
              '.MuiTableCell-root': {
                height: '64px',
                padding: '16px',
                fontFamily: 'Roboto, sans-serif',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '16px',
                color: '#293139',
                borderBottom: '0.5px solid #CDCDCD',
                whiteSpace: 'nowrap',
                verticalAlign: 'middle',
              },
              '.MuiTableRow-root:hover': {
                backgroundColor: '#FFFFFF',
              },
            }}
          />
        </Box>
      </Box>
    </CommonLayout>
  );
}
